import { BrowserAgent } from '@newrelic/browser-agent/dist/esm/loaders/browser-agent';
import { Ajax } from '@newrelic/browser-agent/dist/esm/features/ajax/instrument';
import { JSErrors } from '@newrelic/browser-agent/dist/esm/features/jserrors/instrument';
import { Metrics } from '@newrelic/browser-agent/dist/esm/features/metrics/instrument';
import { PageAction } from '@newrelic/browser-agent/dist/esm/features/page_action/instrument';
import { PageViewEvent } from '@newrelic/browser-agent/dist/esm/features/page_view_event/instrument';
import { PageViewTiming } from '@newrelic/browser-agent/dist/esm/features/page_view_timing/instrument';
import { SessionTrace } from '@newrelic/browser-agent/dist/esm/features/session_trace/instrument';
import { Spa } from '@newrelic/browser-agent/dist/esm/features/spa/instrument';

let newRelicAgent = null;

/**
 * Initialize New Relic Browser Agent
 * @param {Object} templateConfig - Template configuration object
 * @param {string} userEmail - Optional user email
 */
export const initializeNewRelic = (templateConfig, userEmail) => {
  try {
    // Check if window.newrelic already exists (from script tag)
    if (window.newrelic) {
      // If script tag has setCurrentRouteName method, it's the new script tag - skip BrowserAgent initialization
      if (typeof window.newrelic.setCurrentRouteName === 'function') {
        console.log('New Relic: Using existing script tag (new format)');
        return;
      }
      // If script tag exists but doesn't have setCurrentRouteName, it's old script tag - initialize BrowserAgent
      console.log('New Relic: Existing script tag detected (old format), initializing BrowserAgent');
    }

    const newRelicConfig = templateConfig?.pages?.features?.newRelic;

    if (!newRelicConfig) {
      console.warn('New Relic: Configuration not found in templateConfig');
      return;
    }

    const variables = {
      licenseKey: newRelicConfig.licenseKey,
      applicationID: newRelicConfig.applicationID,
      agentID: newRelicConfig.agentID,
      trustKey: newRelicConfig.trustKey,
      accountID: newRelicConfig.accountID,
      beacon: newRelicConfig.beacon || 'bam.nr-data.net',
      errorBeacon: newRelicConfig.errorBeacon || 'bam.nr-data.net',
      sa: newRelicConfig.sa || '1',
    };

    // Validate required fields
    if (!variables.licenseKey || !variables.applicationID || !variables.agentID || !variables.trustKey) {
      console.warn('New Relic: Missing required configuration fields');
      return;
    }

    // Determine if masking should be applied (check silentTracking or other config)
    const silentTrackingNewRelic = newRelicConfig?.silentTracking ?? templateConfig?.pages?.features?.fullStory?.silentTracking ?? true;
    const shouldApplyMasking = !silentTrackingNewRelic;

    // Initialize BrowserAgent
    newRelicAgent = new BrowserAgent({
      init: {
        session_replay: {
          enabled: true,
          block_selector: 'nr-block',
          mask_text_selector: 'nr-mask',
          sampling_rate: 100.0,
          error_sampling_rate: 100.0,
          mask_all_inputs: false,
          collect_fonts: true,
          inline_images: false,
          inline_stylesheet: true,
          fix_stylesheets: true,
          preload: true,
          mask_input_options: shouldApplyMasking ? {} : { password: false },
        },
        distributed_tracing: {
          enabled: true, // CRITICAL: Enables spans for API requests
        },
        privacy: {
          cookies_enabled: true,
          maskAllInputs: false,
        },
        ajax: {
          deny_list: ['bam.nr-data.net'],
          enabled: true,
          harvestTimeSeconds: 10,
        },
      },
      loader_config: {
        accountID: String(variables.accountID || variables.trustKey),
        trustKey: String(variables.trustKey),
        agentID: String(variables.agentID),
        licenseKey: String(variables.licenseKey),
        applicationID: String(variables.applicationID),
      },
      info: {
        beacon: String(variables.beacon),
        errorBeacon: String(variables.errorBeacon),
        licenseKey: String(variables.licenseKey),
        applicationID: String(variables.applicationID),
        sa: String(variables.sa),
      },
      features: [Ajax, JSErrors, Metrics, PageAction, PageViewEvent, PageViewTiming, SessionTrace, Spa],
    });

    // Set user email if provided
    if (userEmail && window.newrelic) {
      updateNewRelicUser(userEmail);
    }

    console.log('New Relic: Browser Agent initialized successfully');
  } catch (error) {
    console.error('New Relic: Error initializing agent', error);
  }
};

/**
 * Update New Relic user identifier
 * @param {string} userEmail - User email address
 */
export const updateNewRelicUser = (userEmail) => {
  try {
    if (window.newrelic && userEmail) {
      window.newrelic.setUserId(userEmail);
      console.log('New Relic: User ID set to', userEmail);
    }
  } catch (error) {
    console.error('New Relic: Error setting user ID', error);
  }
};

/**
 * Set custom New Relic attribute
 * @param {string} key - Attribute key
 * @param {string|number} value - Attribute value
 */
export const setCustomNewRelicAttribute = (key, value) => {
  try {
    if (window.newrelic && key && (typeof value === 'string' || typeof value === 'number' || value === null)) {
      window.newrelic.setCustomAttribute(key, value);
    }
  } catch (error) {
    console.error('New Relic: Error setting custom attribute', error);
  }
};

/**
 * Track SPA page view
 * @param {string} routeName - Route name/path
 */
export const trackPageView = (routeName) => {
  try {
    if (window.newrelic && routeName) {
      window.newrelic.setCurrentRouteName(routeName);
      console.log('New Relic: Page view tracked', routeName);
    }
  } catch (error) {
    console.error('New Relic: Error tracking page view', error);
  }
};

/**
 * Get New Relic agent instance (for debugging)
 * @returns {BrowserAgent|null} Agent instance
 */
export const getNewRelicAgent = () => {
  return newRelicAgent;
};

/**
 * Debug helper - exposes debug function to window
 */
export const debugNewRelic = () => {
  if (typeof window !== 'undefined') {
    window.debugNewRelic = () => {
      console.log('New Relic Debug Info:');
      console.log('- Agent:', newRelicAgent);
      console.log('- window.newrelic:', window.newrelic);
      console.log('- Agent methods:', window.newrelic ? Object.keys(window.newrelic) : 'N/A');
      return {
        agent: newRelicAgent,
        windowNewrelic: window.newrelic,
        methods: window.newrelic ? Object.keys(window.newrelic) : [],
      };
    };
  }
};

// Initialize debug helper
debugNewRelic();
